import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatInteger } from '@/lib/language';
import { trackConversion } from '@/lib/analytics';
import { useJourney } from '@/hooks/useJourney';
import { useSelection } from '@/contexts/SelectionContext';
import { LIVE_FEED_STATUS_QUERY_KEY } from '@/hooks/useLiveFeedStatus';
import { actionTargetFor } from '@/lib/journeyTargets';
import type { JourneyStepId } from '@/lib/journey';

/**
 * Der naechste Schritt als Knopf, dort wo der Nutzer gerade fertig geworden
 * ist (Spec `docs/superpowers/specs/2026-09-16-journeybar-statusfluss-design.md`,
 * Abschnitt 7).
 *
 * EIN REGELWERK: Ziel und Beschriftung kommen aus `useJourney().targets` und
 * der i18n-Tabelle `common:journey.handoff.*`. Dieses Bauteil rechnet den
 * naechsten Schritt NIE selbst aus -- wer ihn aendern will, aendert
 * `deriveLadderStep` in `src/lib/journey.ts`, und alle sechs Uebergabestellen
 * wandern mit. Der Aufrufer sagt nur, WELCHER Schritt gerade ansteht und mit
 * welcher Zahl.
 *
 * ZWEI ZAHLEN, ZWEI QUELLEN, und das ist Absicht (Spec 7, "Zahl aus dem
 * lokalen Ergebnis, Ziel aus der Leiter"): die Zahl am Knopf beschreibt, was
 * GERADE passiert ist ("12 freigegeben"), und kommt vom Aufrufer, weil
 * `useProductStats` ausserhalb eines laufenden Jobs nicht pollt und die
 * Statistik in diesem Moment alt sein kann. Das Ziel kommt aus der Leiter.
 */
export interface NextStepCtaProps {
  /**
   * Der Schritt, der ANGEBOTEN wird. `ads_campaigns` ist bewusst
   * ausgeschlossen: Schritt 6 ist ein Ausblick ohne Handlung (Spec 3, D4),
   * ein Knopf dorthin haette kein Ziel ausser dem Teaser.
   */
  step: Exclude<JourneyStepId, 'ads_campaigns'>;
  /** Der gerade abgeschlossene Schritt, nur fuer die Auswertung. */
  from: JourneyStepId;
  /** Die Menge, um die es geht. `null`/`undefined` heisst "ohne Zahl". */
  count?: number | null;
  variant?: 'default' | 'outline';
  className?: string;
  /**
   * Statt zu navigieren: die Handlung auf DIESER Seite ausloesen (Upload-
   * Dialog, Optimieren-Lauf, Kapitel aufklappen). Wird sie uebergeben, gibt es
   * keinen Routenwechsel -- der Nutzer bleibt, wo er ist.
   */
  onAction?: () => void;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Welche Zahlen nach einer Uebergabe neu gelesen werden muessen, damit Chips
 * und Handlungskarte den neuen Stand zeigen, BEVOR der Nutzer klickt.
 *
 * Alle drei Familien haengen an Mengen, die sich durch genau die Handlung
 * geaendert haben, die eben fertig wurde: die Produktstatistik speist die
 * Leiter, die Reiterzahlen den Werkbank-Kopf, der Live-Stand das Kapitel
 * "Bei Google".
 */
export function invalidateJourneyCounts(
  queryClient: ReturnType<typeof useQueryClient>,
  feedId: string | null,
): void {
  queryClient.invalidateQueries({ queryKey: ['simple-product-stats', feedId] });
  queryClient.invalidateQueries({ queryKey: ['workbench-status-counts', feedId] });
  queryClient.invalidateQueries({ queryKey: [LIVE_FEED_STATUS_QUERY_KEY, feedId] });
}

export function NextStepCta({
  step,
  from,
  count,
  variant = 'default',
  className,
  onAction,
  disabled = false,
  loading = false,
}: NextStepCtaProps) {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const journey = useJourney();
  const { selectedFeedId } = useSelection();

  const ziel = actionTargetFor(step, journey.targets);

  /**
   * Die Beschriftung. Drei Sonderfaelle, alle gemessen und nicht kosmetisch:
   *
   * - `optimize` bei erschoepftem Kontingent: das Ziel ist ohnehin `/pricing`
   *   (aus `targets`), und die Beschriftung muss das sagen, sonst verspricht
   *   der Knopf eine Optimierung und liefert eine Preisseite.
   * - `optimize` mit `count === 0` und freiem Kontingent: es gibt nichts zu
   *   optimieren, ein Knopf "0 Produkte optimieren" fuehrte in einen Lauf ohne
   *   Menge. Dann der neutrale Weg zurueck zum Feed-Check -- und weil dieses
   *   Wort kein Ziel in `targets` hat, MUSS der Aufrufer hier ein `onAction`
   *   mitgeben (Plan-Entscheidung E7: Dialog schliessen). Ohne es navigierte
   *   der Knopf auf `targets.optimize`, also in die Werkbank, und Beschriftung
   *   und Ziel widersprachen sich.
   * - `ads_connect`, wenn Schritt 5 schon erledigt ist: dann ist die Handlung
   *   nicht mehr "verbinden", sondern "die Zahlen ansehen". Ob er erledigt
   *   ist, weiss die Leiter, nicht der Aufrufer.
   *
   * Die Reihenfolge ist tragend: `quotaBlocked` steht VOR `count === 0`, weil
   * `check_can_optimize` v4 bei erschoepftem Kontingent `remaining_capacity = 0`
   * liefert und das Angebot damit ebenfalls 0 ist. Stand `count === 0` vorne,
   * war der `pricing`-Zweig in Produktion unerreichbar und der Knopf sagte
   * "Zum Feed-Check", waehrend sein Ziel die Preisseite war.
   */
  const adsFertig = journey.steps.find((s) => s.id === 'ads_connect')?.state === 'done';
  const schluessel =
    step === 'optimize' && journey.quotaBlocked
      ? 'journey.handoff.pricing'
      : step === 'optimize' && (count ?? 0) === 0
        ? 'journey.handoff.toFeedCheck'
        : step === 'ads_connect'
          ? (adsFertig ? 'journey.handoff.ads_numbers' : 'journey.handoff.ads_connect')
          : `journey.handoff.${step}`;

  const label = t(schluessel, {
    count: count ?? 0,
    formatted: formatInteger(count ?? 0, i18n?.language),
  });

  // Mitglied ohne eigene Verknuepfung: Schritt 5 liegt beim Eigentuemer, es
  // gibt keine Handlung anzubieten (A2-NEU-1).
  const adsBeimEigentuemer = journey.steps.find((s) => s.id === 'ads_connect')?.state === 'skipped';

  const handleClick = useCallback(() => {
    trackConversion('journey_handoff_click', { from, to: step, target: ziel });
    // Zuerst die Zahlen entwerten, dann handeln: die Zielseite soll den neuen
    // Stand laden und nicht den, der vor dem gerade abgeschlossenen Vorgang
    // galt.
    invalidateJourneyCounts(queryClient, selectedFeedId);
    if (onAction) {
      onAction();
      return;
    }
    navigate(ziel);
  }, [from, step, ziel, queryClient, selectedFeedId, onAction, navigate]);

  if (step === 'ads_connect' && adsBeimEigentuemer) return null;

  return (
    <Button
      onClick={handleClick}
      variant={variant}
      className={className}
      disabled={disabled}
      loading={loading}
      data-testid="next-step-cta"
    >
      {!loading && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
      {label}
    </Button>
  );
}
